import {
  Badge,
  ExternalLinkIcon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@commander/ui';
import { useMemo, useState } from 'react';
import { useCommanderGoAnalytics } from '../../hooks/useCommanderGoAnalytics.js';
import type { DataTableBlock, DataTableColumn, DataTableDeepLink } from '../blocks/table.js';
import { ExpandableBlock, ExpandTrigger, useExpandable } from './ExpandableBlock.js';
import {
  createSortComparator,
  formatValue,
  getStatusBadgeVariant,
  resolveDeepLink,
  trackDeepLinkClick,
} from './utils.js';

const INLINE_ROW_LIMIT = 5;

// --- Helpers ---

function getDefaultAlign(type?: string): 'left' | 'right' | 'center' {
  if (type === 'number' || type === 'currency' || type === 'percent') return 'right';
  return 'left';
}

function isDefaultSortable(type?: string): boolean {
  return type === 'number' || type === 'currency' || type === 'date' || type === 'percent';
}

function findDeepLink(column: DataTableColumn, deepLinks?: DataTableDeepLink[]) {
  return deepLinks?.find((dl) => dl.column === column.key);
}

// --- Sort Icon ---

function SortIcon({ active, direction }: { active: boolean; direction: 'asc' | 'desc' }) {
  if (!active) {
    return (
      <svg
        className="ml-1 inline-block h-3 w-3 text-muted-foreground/50"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        aria-hidden="true"
      >
        <path d="M7 15l5 5 5-5" />
        <path d="M7 9l5-5 5 5" />
      </svg>
    );
  }
  return (
    <svg
      className="ml-1 inline-block h-3 w-3 text-foreground"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      {direction === 'asc' ? <path d="M7 15l5 5 5-5" /> : <path d="M7 9l5-5 5 5" />}
    </svg>
  );
}

// --- Cell Renderer ---

function CellValue({
  value,
  column,
  row,
  deepLink,
  onDeepLinkClick,
}: {
  value: unknown;
  column: DataTableColumn;
  row: Record<string, unknown>;
  deepLink?: DataTableDeepLink;
  onDeepLinkClick?: (href: string) => void;
}) {
  const formatted = formatValue(value, column.type);

  // Status badge — either the agent marked the column explicitly, or the
  // column's key/label reads as "status" (agents don't always set type).
  const isStatusColumn =
    column.type === 'status' ||
    column.key.toLowerCase() === 'status' ||
    column.label?.toLowerCase() === 'status';
  if (isStatusColumn && typeof value === 'string') {
    return <Badge variant={getStatusBadgeVariant(value, column.status_colors)}>{formatted}</Badge>;
  }

  // Deep link
  if (deepLink) {
    const href = resolveDeepLink(deepLink.pattern, row, deepLink.id_field);
    if (href) {
      return (
        <a
          href={href}
          onClick={() => onDeepLinkClick?.(href)}
          className="inline-flex items-center gap-1 rounded-md border border-primary-200 bg-primary-50 px-1.5 py-0.5 text-xs font-medium text-primary-700 no-underline hover:bg-primary-100 dark:border-primary-800 dark:bg-primary-950 dark:text-primary-300 dark:hover:bg-primary-900"
        >
          {formatted}
          <ExternalLinkIcon className="h-3 w-3 shrink-0" />
        </a>
      );
    }
  }

  return <>{formatted}</>;
}

// --- Table Content (shared between inline and expanded) ---

function TableContent({
  data,
  sortKey,
  sortDirection,
  onSort,
  maxRows,
  onDeepLinkClick,
}: {
  data: DataTableBlock;
  sortKey: string | null;
  sortDirection: 'asc' | 'desc';
  onSort: (key: string) => void;
  maxRows?: number;
  onDeepLinkClick?: (href: string) => void;
}) {
  const expandable = useExpandable();
  const sortedRows = useMemo(() => {
    if (!sortKey) return data.rows;
    const col = data.columns.find((c) => c.key === sortKey);
    const comparator = createSortComparator(sortKey, col?.type, sortDirection);
    return [...data.rows].sort(comparator);
  }, [data.rows, data.columns, sortKey, sortDirection]);

  const displayRows = maxRows != null ? sortedRows.slice(0, maxRows) : sortedRows;
  const totalCount = data.total_count ?? data.rows.length;
  const isInlineTruncated = maxRows != null && data.rows.length > maxRows;
  const isSqlTruncated =
    data.truncated || (data.total_count != null && data.total_count > data.rows.length);

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {data.columns.map((col) => {
              const align = col.align ?? getDefaultAlign(col.type);
              const sortable = col.sortable ?? isDefaultSortable(col.type);
              const isActive = sortKey === col.key;

              return (
                <TableHead
                  key={col.key}
                  className={`whitespace-nowrap text-xs ${align === 'right' ? 'text-right' : ''} ${
                    sortable ? 'cursor-pointer select-none hover:bg-muted/50' : ''
                  }`}
                  role={sortable ? 'button' : undefined}
                  tabIndex={sortable ? 0 : undefined}
                  aria-sort={
                    isActive ? (sortDirection === 'asc' ? 'ascending' : 'descending') : undefined
                  }
                  onClick={sortable ? () => onSort(col.key) : undefined}
                  onKeyDown={
                    sortable
                      ? (e: React.KeyboardEvent) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onSort(col.key);
                          }
                        }
                      : undefined
                  }
                >
                  {col.label}
                  {sortable && (
                    <SortIcon active={isActive} direction={isActive ? sortDirection : 'asc'} />
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={data.columns.length}
                className="py-6 text-center text-xs text-muted-foreground"
              >
                No results found
              </TableCell>
            </TableRow>
          ) : (
            displayRows.map((row, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: row order is stable (controlled by sort state)
              <TableRow key={rowIndex} className="hover:bg-transparent">
                {data.columns.map((col) => {
                  const align = col.align ?? getDefaultAlign(col.type);
                  const deepLink = findDeepLink(col, data.deep_links);
                  return (
                    <TableCell
                      key={col.key}
                      className={`whitespace-nowrap text-xs ${align === 'right' ? 'text-right' : ''}`}
                    >
                      <CellValue
                        value={row[col.key]}
                        column={col}
                        row={row}
                        deepLink={deepLink}
                        onDeepLinkClick={onDeepLinkClick}
                      />
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          )}
        </TableBody>
        {(isInlineTruncated || isSqlTruncated) && (
          <TableFooter>
            <TableRow>
              <TableCell
                colSpan={data.columns.length}
                className="text-center text-xs text-muted-foreground"
              >
                {isInlineTruncated && expandable?.canExpand ? (
                  <button
                    type="button"
                    onClick={() => expandable.setExpanded(true)}
                    className="cursor-pointer hover:underline focus:outline-none focus-visible:underline"
                  >
                    Showing {displayRows.length} of {totalCount} results
                  </button>
                ) : (
                  <>
                    Showing {displayRows.length} of {totalCount} results
                  </>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </div>
  );
}

// --- Main DataTable Component ---

export function DataTable({ data }: { data: DataTableBlock }) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const analyticsCtx = useCommanderGoAnalytics();

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const handleDeepLinkClick = (href: string) => {
    trackDeepLinkClick(analyticsCtx, 'data_table', href);
  };

  const expandedContent = (
    <TableContent
      data={data}
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSort={handleSort}
      onDeepLinkClick={handleDeepLinkClick}
    />
  );

  return (
    <ExpandableBlock title={data.title} expandedContent={expandedContent}>
      <div className="rounded-lg border bg-card text-card-foreground overflow-hidden">
        <div className="flex items-center justify-between gap-2 px-3 py-2">
          <h4 className="text-xs font-semibold">{data.title}</h4>
          <ExpandTrigger />
        </div>
        <TableContent
          data={data}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          maxRows={INLINE_ROW_LIMIT}
          onDeepLinkClick={handleDeepLinkClick}
        />
      </div>
    </ExpandableBlock>
  );
}

// --- Skeleton ---

export function TableSkeleton() {
  return (
    <div className="rounded-lg border bg-card p-4">
      <Skeleton className="mb-4 h-4 w-[40%]" />
      <div className="space-y-3">
        {/* Header row */}
        <div className="flex gap-4">
          <Skeleton className="h-3 w-32" />
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-28" />
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-24" />
        </div>
        {/* Data rows */}
        {['a', 'b', 'c', 'd', 'e'].map((id) => (
          <div key={id} className="flex gap-4">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
          </div>
        ))}
      </div>
    </div>
  );
}
